import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  MapPin, 
  Plus, 
  Filter, 
  Users, 
  ExternalLink,
  AlertTriangle,
  Clock,
  CheckCircle,
  Search,
  Calendar,
  Home,
  Mail,
  MessageCircle,
  Edit,
  Loader2
} from 'lucide-react';
import 'leaflet/dist/leaflet.css';

// Fix for default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const createIcon = (color) => new L.Icon({
  iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${color}.png`,
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const icons = {
  urgent: createIcon('red'),
  en_cours: createIcon('orange'),
  resolu: createIcon('green')
};

const statusLabels = {
  urgent: { label: 'Urgent', color: 'destructive', icon: AlertTriangle },
  en_cours: { label: 'En cours', color: 'warning', icon: Clock },
  resolu: { label: 'Résolu', color: 'success', icon: CheckCircle }
};

const categoryLabels = {
  controle: 'Contrôle',
  mise_en_demeure: 'Mise en demeure',
  proces: 'Procès',
  expulsion: 'Expulsion'
};

const habitatTypes = [
  { id: 'yourte', label: 'Yourte' },
  { id: 'yourlotte', label: 'Yourlotte' },
  { id: 'tiny_house', label: 'Tiny House' },
  { id: 'caravane', label: 'Caravane' },
  { id: 'cabane', label: 'Cabane' },
  { id: 'dome', label: 'Dôme' },
  { id: 'mobil_home', label: 'Mobil-home' },
  { id: 'tipi', label: 'Tipi' },
  { id: 'autre', label: 'Autre' }
];

function MapController({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, zoom || 10);
    }
  }, [center, zoom, map]);
  return null;
}

export default function MapPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [situations, setSituations] = useState([]);
  const [filteredSituations, setFilteredSituations] = useState([]);
  const [selectedSituation, setSelectedSituation] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(searchParams.get('action') === 'add');
  const [showSupportDialog, setShowSupportDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [mapCenter, setMapCenter] = useState([46.603354, 1.888334]);
  const [mapZoom] = useState(5); // Zoom to see all of France
  const [loading, setLoading] = useState(true);

  // Address autocomplete
  const [addressQuery, setAddressQuery] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [searchingAddress, setSearchingAddress] = useState(false);

  // Form states
  const [newSituation, setNewSituation] = useState({
    title: '',
    description: '',
    address: '',
    latitude: '',
    longitude: '',
    status: 'en_cours',
    category: 'controle',
    habitat_type: 'yourte',
    needs_support: true,
    support_link: '',
    contact_email: ''
  });

  const [supportForm, setSupportForm] = useState({
    name: '',
    email: '',
    message: '',
    support_type: 'aide_concrete'
  });

  // Edit form state
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    status: '',
    support_link: '',
    contact_email: '',
    needs_support: true
  });
  const [savingEdit, setSavingEdit] = useState(false);

  // Captcha state
  const [captcha, setCaptcha] = useState({ id: '', question: '' });
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [loadingCaptcha, setLoadingCaptcha] = useState(false);

  // Fill edit form when opening edit dialog
  useEffect(() => {
    if (showEditDialog && selectedSituation) {
      setEditForm({
        title: selectedSituation.title || '',
        description: selectedSituation.description || '',
        status: selectedSituation.status || 'en_cours',
        support_link: selectedSituation.support_link || '',
        contact_email: selectedSituation.contact_email || '',
        needs_support: selectedSituation.needs_support ?? true
      });
    }
  }, [showEditDialog, selectedSituation]);

  useEffect(() => {
    fetchSituations();
  }, []);

  // Open situation detail when URL has ?situation=ID
  useEffect(() => {
    const situationId = searchParams.get('situation');
    const editMode = searchParams.get('edit') === 'true';
    
    if (situationId && situations.length > 0) {
      const situation = situations.find(s => s.id === situationId);
      if (situation) {
        setSelectedSituation(situation);
        setMapCenter([situation.latitude, situation.longitude]);
        
        // If edit mode and user is owner, open edit dialog directly
        if (editMode && user && situation.user_id === user.id) {
          setShowEditDialog(true);
        } else {
          setShowDetailDialog(true);
        }
      }
    }
  }, [searchParams, situations, user]);

  useEffect(() => {
    if (showSupportDialog) {
      fetchCaptcha();
    }
  }, [showSupportDialog]);

  useEffect(() => {
    let filtered = [...situations];
    if (statusFilter !== 'all') {
      filtered = filtered.filter(s => s.status === statusFilter);
    }
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(s => s.category === categoryFilter);
    }
    setFilteredSituations(filtered);
  }, [situations, statusFilter, categoryFilter]);

  const fetchCaptcha = async () => {
    setLoadingCaptcha(true);
    try {
      const response = await axios.get(`${API}/captcha`);
      setCaptcha({ id: response.data.captcha_id, question: response.data.question });
      setCaptchaAnswer('');
    } catch (error) {
      console.error('Failed to fetch captcha:', error);
    } finally {
      setLoadingCaptcha(false);
    }
  };

  const fetchSituations = async () => {
    try {
      const response = await axios.get(`${API}/situations`);
      setSituations(response.data);
      setFilteredSituations(response.data);
    } catch (error) {
      console.error('Failed to fetch situations:', error);
      toast.error('Erreur lors du chargement des situations');
    } finally {
      setLoading(false);
    }
  };

  // Debounced address search
  const searchAddress = useCallback(async (query) => {
    if (query.length < 3) {
      setAddressSuggestions([]);
      return;
    }
    setSearchingAddress(true);
    try {
      const response = await axios.get(`${API}/geocode`, { params: { q: query } });
      setAddressSuggestions(response.data);
    } catch (error) {
      console.error('Geocoding error:', error);
    } finally {
      setSearchingAddress(false);
    }
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (addressQuery) {
        searchAddress(addressQuery);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [addressQuery, searchAddress]);

  const selectAddress = (suggestion) => {
    setNewSituation({
      ...newSituation,
      address: suggestion.display_name,
      latitude: suggestion.lat,
      longitude: suggestion.lon
    });
    setAddressQuery(suggestion.display_name);
    setAddressSuggestions([]);
  };

  const handleAddSituation = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Vous devez être connecté pour ajouter une situation');
      return;
    }
    if (!newSituation.latitude || !newSituation.longitude) {
      toast.error('Veuillez sélectionner une adresse dans la liste');
      return;
    }
    try {
      const data = {
        ...newSituation,
        latitude: parseFloat(newSituation.latitude),
        longitude: parseFloat(newSituation.longitude),
        photos: [],
        timeline: []
      };
      await axios.post(`${API}/situations`, data);
      toast.success('Situation ajoutée avec succès');
      setShowAddDialog(false);
      setNewSituation({
        title: '',
        description: '',
        address: '',
        latitude: '',
        longitude: '',
        status: 'en_cours',
        category: 'controle',
        habitat_type: 'yourte',
        needs_support: true,
        support_link: '',
        contact_email: ''
      });
      setAddressQuery('');
      fetchSituations();
    } catch (error) {
      console.error('Failed to add situation:', error);
      toast.error('Erreur lors de l\'ajout de la situation');
    }
  };

  const handleSupport = async (e) => {
    e.preventDefault();
    if (!captchaAnswer) {
      toast.error('Veuillez répondre au captcha');
      return;
    }
    try {
      await axios.post(`${API}/support`, {
        ...supportForm,
        situation_id: selectedSituation.id,
        captcha_id: captcha.id,
        captcha_answer: parseInt(captchaAnswer, 10)
      });
      toast.success('Merci pour votre soutien ! La personne concernée recevra votre message par email.');
      setShowSupportDialog(false);
      setSupportForm({ name: '', email: '', message: '', support_type: 'aide_concrete' });
      setCaptchaAnswer('');
      fetchSituations();
    } catch (error) {
      console.error('Failed to submit support:', error);
      const msg = error.response?.data?.detail || 'Erreur lors de l\'envoi du soutien';
      toast.error(msg);
      fetchCaptcha();
    }
  };

  const openDetailDialog = (situation) => {
    setSelectedSituation(situation);
    setShowDetailDialog(true);
  };

  const getHabitatLabel = (type) => {
    const habitat = habitatTypes.find(h => h.id === type);
    return habitat ? habitat.label : type;
  };

  return (
    <div className="min-h-screen" data-testid="map-page">
      {/* Header */}
      <div className="bg-muted/30 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">
            Cartographie des situations
          </h1>
          <p className="text-muted-foreground">
            Visualisez les situations en cours et proposez votre soutien.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar */}
          <div className="lg:w-80 space-y-4">
            {/* Filters */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Filter className="w-4 h-4" />
                  Filtres
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Statut</Label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger data-testid="status-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                      <SelectItem value="en_cours">En cours</SelectItem>
                      <SelectItem value="resolu">Résolu</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Catégorie</Label>
                  <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                    <SelectTrigger data-testid="category-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes</SelectItem>
                      <SelectItem value="controle">Contrôle</SelectItem>
                      <SelectItem value="mise_en_demeure">Mise en demeure</SelectItem>
                      <SelectItem value="proces">Procès</SelectItem>
                      <SelectItem value="expulsion">Expulsion</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Add situation button */}
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button className="w-full gap-2" data-testid="add-situation-button">
                  <Plus className="w-4 h-4" />
                  Référencer ma situation
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Référencer ma situation</DialogTitle>
                </DialogHeader>
                {!user ? (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground mb-4">
                      Vous devez être connecté pour ajouter une situation.
                    </p>
                    <Button asChild>
                      <a href="/connexion">Se connecter</a>
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleAddSituation} className="space-y-4">
                    <div>
                      <Label htmlFor="title">Titre de votre situation *</Label>
                      <Input
                        id="title"
                        value={newSituation.title}
                        onChange={(e) => setNewSituation({ ...newSituation, title: e.target.value })}
                        placeholder="Ex: Yourte en Ardèche - Mise en demeure"
                        required
                        data-testid="situation-title-input"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="address">Adresse *</Label>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="address"
                          value={addressQuery}
                          onChange={(e) => {
                            setAddressQuery(e.target.value);
                            setNewSituation({ ...newSituation, address: e.target.value, latitude: '', longitude: '' });
                          }}
                          placeholder="Tapez votre adresse..."
                          className="pl-9"
                          required
                          data-testid="situation-address-input"
                        />
                        {searchingAddress && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                          </div>
                        )}
                      </div>
                      {addressSuggestions.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
                          {addressSuggestions.map((suggestion, index) => (
                            <button
                              key={index}
                              type="button"
                              className="w-full px-3 py-2 text-left text-sm hover:bg-muted transition-colors"
                              onClick={() => selectAddress(suggestion)}
                            >
                              {suggestion.display_name}
                            </button>
                          ))}
                        </div>
                      )}
                      {newSituation.latitude && (
                        <p className="text-xs text-green-600 mt-1">
                          ✓ Adresse géolocalisée
                        </p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="description">Description détaillée *</Label>
                      <Textarea
                        id="description"
                        value={newSituation.description}
                        onChange={(e) => setNewSituation({ ...newSituation, description: e.target.value })}
                        placeholder="Décrivez votre situation, les étapes passées, vos besoins..."
                        rows={4}
                        required
                        data-testid="situation-description-input"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Type d'habitat</Label>
                        <Select 
                          value={newSituation.habitat_type} 
                          onValueChange={(v) => setNewSituation({ ...newSituation, habitat_type: v })}
                        >
                          <SelectTrigger data-testid="situation-habitat-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {habitatTypes.map(type => (
                              <SelectItem key={type.id} value={type.id}>{type.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Catégorie</Label>
                        <Select 
                          value={newSituation.category} 
                          onValueChange={(v) => setNewSituation({ ...newSituation, category: v })}
                        >
                          <SelectTrigger data-testid="situation-category-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="controle">Contrôle</SelectItem>
                            <SelectItem value="mise_en_demeure">Mise en demeure</SelectItem>
                            <SelectItem value="proces">Procès</SelectItem>
                            <SelectItem value="expulsion">Expulsion</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Statut</Label>
                        <Select 
                          value={newSituation.status} 
                          onValueChange={(v) => setNewSituation({ ...newSituation, status: v })}
                        >
                          <SelectTrigger data-testid="situation-status-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="en_cours">En cours</SelectItem>
                            <SelectItem value="urgent">Urgent</SelectItem>
                            <SelectItem value="resolu">Résolu</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="contact_email">Email de contact</Label>
                        <Input
                          id="contact_email"
                          type="email"
                          value={newSituation.contact_email}
                          onChange={(e) => setNewSituation({ ...newSituation, contact_email: e.target.value })}
                          placeholder="votre@email.com"
                          data-testid="situation-email-input"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="support_link">Lien de soutien (cagnotte, pétition...)</Label>
                      <Input
                        id="support_link"
                        type="url"
                        value={newSituation.support_link}
                        onChange={(e) => setNewSituation({ ...newSituation, support_link: e.target.value })}
                        placeholder="https://..."
                        data-testid="situation-support-link-input"
                      />
                    </div>

                    <Button type="submit" className="w-full" data-testid="submit-situation-button">
                      Publier ma situation
                    </Button>
                  </form>
                )}
              </DialogContent>
            </Dialog>

            {/* Legend */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Légende</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-red-500 rounded-full" />
                  <span className="text-sm">Urgent</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-orange-500 rounded-full" />
                  <span className="text-sm">En cours</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-green-500 rounded-full" />
                  <span className="text-sm">Résolu</span>
                </div>
              </CardContent>
            </Card>

            {/* Stats */}
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary">{situations.length}</div>
                  <div className="text-sm text-muted-foreground">situations référencées</div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Map */}
          <div className="flex-1">
            <Card className="overflow-hidden">
              <div className="h-[600px]" data-testid="map-container">
                <MapContainer
                  center={mapCenter}
                  zoom={mapZoom}
                  style={{ height: '100%', width: '100%', zIndex: 0 }}
                  scrollWheelZoom={true}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <MapController center={mapCenter} zoom={mapZoom} />
                  {filteredSituations.map((situation) => (
                    <Marker
                      key={situation.id}
                      position={[situation.latitude, situation.longitude]}
                      icon={icons[situation.status] || icons.en_cours}
                      eventHandlers={{
                        click: () => setSelectedSituation(situation)
                      }}
                    >
                      <Popup>
                        <div className="min-w-[280px]">
                          <h3 className="font-bold text-base mb-2">{situation.title}</h3>
                          <div className="flex flex-wrap gap-2 mb-2">
                            <Badge variant={situation.status === 'urgent' ? 'destructive' : situation.status === 'resolu' ? 'secondary' : 'default'}>
                              {statusLabels[situation.status]?.label}
                            </Badge>
                            <Badge variant="outline">{categoryLabels[situation.category]}</Badge>
                            {situation.habitat_type && (
                              <Badge variant="secondary">{getHabitatLabel(situation.habitat_type)}</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                            {situation.description}
                          </p>
                          <div className="text-xs text-muted-foreground mb-3">
                            <MapPin className="w-3 h-3 inline mr-1" />
                            {situation.address}
                          </div>
                          <div className="flex items-center gap-2 text-sm mb-3">
                            <Users className="w-4 h-4" />
                            <span>{situation.support_count} soutiens</span>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => openDetailDialog(situation)}
                            >
                              Plus d'infos
                            </Button>
                            {situation.needs_support && (
                              <Button 
                                size="sm"
                                onClick={() => {
                                  setSelectedSituation(situation);
                                  setShowSupportDialog(true);
                                }}
                              >
                                Soutenir
                              </Button>
                            )}
                          </div>
                          {situation.support_link && (
                            <a 
                              href={situation.support_link} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="block mt-2"
                            >
                              <Button size="sm" variant="outline" className="w-full gap-2">
                                <ExternalLink className="w-3 h-3" />
                                Lien de soutien
                              </Button>
                            </a>
                          )}
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedSituation?.title}</DialogTitle>
          </DialogHeader>
          {selectedSituation && (
            <div className="space-y-6">
              <div className="flex flex-wrap gap-2">
                <Badge variant={selectedSituation.status === 'urgent' ? 'destructive' : selectedSituation.status === 'resolu' ? 'secondary' : 'default'}>
                  {statusLabels[selectedSituation.status]?.label}
                </Badge>
                <Badge variant="outline">{categoryLabels[selectedSituation.category]}</Badge>
                {selectedSituation.habitat_type && (
                  <Badge variant="secondary" className="gap-1">
                    <Home className="w-3 h-3" />
                    {getHabitatLabel(selectedSituation.habitat_type)}
                  </Badge>
                )}
              </div>

              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <MapPin className="w-4 h-4" />
                {selectedSituation.address}
              </div>

              <div>
                <h4 className="font-semibold mb-2">Description</h4>
                <p className="text-muted-foreground whitespace-pre-wrap">{selectedSituation.description}</p>
              </div>

              {selectedSituation.timeline && selectedSituation.timeline.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Chronologie
                  </h4>
                  <div className="space-y-3 border-l-2 border-primary/30 pl-4">
                    {selectedSituation.timeline.map((event, index) => (
                      <div key={index} className="relative">
                        <div className="absolute -left-[21px] w-3 h-3 rounded-full bg-primary" />
                        <div className="text-xs text-muted-foreground">{event.date}</div>
                        <div className="text-sm">{event.event}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  {selectedSituation.support_count} soutiens
                </div>
                {selectedSituation.contact_email && (
                  <div className="flex items-center gap-1">
                    <Mail className="w-4 h-4" />
                    Contact disponible
                  </div>
                )}
              </div>

              <div className="flex gap-3 flex-wrap">
                {/* Edit button - only for situation owner */}
                {user && selectedSituation.user_id === user.id && (
                  <Button 
                    variant="secondary"
                    onClick={() => {
                      setShowDetailDialog(false);
                      setShowEditDialog(true);
                    }}
                    className="gap-2"
                    data-testid="edit-situation-btn"
                  >
                    <Calendar className="w-4 h-4" />
                    Modifier ma situation
                  </Button>
                )}
                {/* Message button - contact the situation owner */}
                {user && selectedSituation.user_id !== user.id && (
                  <Button 
                    variant="outline"
                    onClick={() => {
                      navigate(`/messages?to=${selectedSituation.user_id}&name=${encodeURIComponent(selectedSituation.user_name)}`);
                    }}
                    className="gap-2"
                    data-testid="contact-owner-btn"
                  >
                    <MessageCircle className="w-4 h-4" />
                    Contacter {selectedSituation.user_name}
                  </Button>
                )}
                {selectedSituation.needs_support && (
                  <Button 
                    onClick={() => {
                      setShowDetailDialog(false);
                      setShowSupportDialog(true);
                    }}
                    className="gap-2"
                  >
                    Proposer mon soutien
                  </Button>
                )}
                {selectedSituation.support_link && (
                  <a href={selectedSituation.support_link} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" className="gap-2">
                      <ExternalLink className="w-4 h-4" />
                      Lien de soutien
                    </Button>
                  </a>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Support Dialog */}
      <Dialog open={showSupportDialog} onOpenChange={setShowSupportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Proposer mon soutien</DialogTitle>
          </DialogHeader>
          {selectedSituation && (
            <form onSubmit={handleSupport} className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Votre message sera transmis à {selectedSituation.user_name} qui pourra vous contacter.
              </p>
              <div>
                <Label htmlFor="support-name">Votre nom *</Label>
                <Input
                  id="support-name"
                  value={supportForm.name}
                  onChange={(e) => setSupportForm({ ...supportForm, name: e.target.value })}
                  required
                  data-testid="support-name-input"
                />
              </div>
              <div>
                <Label htmlFor="support-email">Votre email *</Label>
                <Input
                  id="support-email"
                  type="email"
                  value={supportForm.email}
                  onChange={(e) => setSupportForm({ ...supportForm, email: e.target.value })}
                  required
                  data-testid="support-email-input"
                />
              </div>
              <div>
                <Label>Type de soutien</Label>
                <Select 
                  value={supportForm.support_type} 
                  onValueChange={(v) => setSupportForm({ ...supportForm, support_type: v })}
                >
                  <SelectTrigger data-testid="support-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="presence">Présence au procès</SelectItem>
                    <SelectItem value="relay">Relais médiatique</SelectItem>
                    <SelectItem value="aide_concrete">Aide concrète</SelectItem>
                    <SelectItem value="soutien_juridique">Soutien juridique</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="support-message">Message *</Label>
                <Textarea
                  id="support-message"
                  value={supportForm.message}
                  onChange={(e) => setSupportForm({ ...supportForm, message: e.target.value })}
                  placeholder="Comment souhaitez-vous aider ?"
                  required
                  data-testid="support-message-input"
                />
              </div>
              <div className="bg-muted/50 p-4 rounded-lg">
                <Label htmlFor="captcha-answer" className="text-sm font-medium">
                  Vérification anti-spam *
                </Label>
                <p className="text-sm text-muted-foreground mt-1 mb-2">
                  {loadingCaptcha ? 'Chargement...' : captcha.question}
                </p>
                <div className="flex gap-2">
                  <Input
                    id="captcha-answer"
                    type="number"
                    value={captchaAnswer}
                    onChange={(e) => setCaptchaAnswer(e.target.value)}
                    placeholder="Votre réponse"
                    required
                    className="flex-1"
                    data-testid="captcha-answer-input"
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={fetchCaptcha}
                    disabled={loadingCaptcha}
                  >
                    Nouveau
                  </Button>
                </div>
              </div>
              <Button type="submit" className="w-full" data-testid="submit-support-button">
                Envoyer mon soutien
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Situation Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Modifier ma situation
            </DialogTitle>
          </DialogHeader>
          {selectedSituation && (
            <form onSubmit={async (e) => {
              e.preventDefault();
              setSavingEdit(true);
              try {
                const token = localStorage.getItem('token');
                await axios.put(`${API}/situations/${selectedSituation.id}`, {
                  title: editForm.title,
                  description: editForm.description,
                  status: editForm.status,
                  support_link: editForm.support_link || null,
                  contact_email: editForm.contact_email || null,
                  needs_support: editForm.needs_support
                }, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Situation mise à jour avec succès !');
                setShowEditDialog(false);
                fetchSituations();
              } catch (error) {
                console.error('Update error:', error);
                toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
              } finally {
                setSavingEdit(false);
              }
            }} className="space-y-4">
              <div>
                <Label htmlFor="edit-title">Titre *</Label>
                <Input
                  id="edit-title"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  required
                  data-testid="edit-title-input"
                />
              </div>
              <div>
                <Label htmlFor="edit-description">Description *</Label>
                <Textarea
                  id="edit-description"
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  required
                  rows={4}
                  data-testid="edit-description-input"
                />
              </div>
              <div>
                <Label htmlFor="edit-status">Statut</Label>
                <Select
                  value={editForm.status}
                  onValueChange={(value) => setEditForm({ ...editForm, status: value })}
                >
                  <SelectTrigger id="edit-status" data-testid="edit-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="urgent">Urgent</SelectItem>
                    <SelectItem value="en_cours">En cours</SelectItem>
                    <SelectItem value="resolu">Résolu</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit-support-link">Lien de pétition / cagnotte</Label>
                <Input
                  id="edit-support-link"
                  type="url"
                  value={editForm.support_link}
                  onChange={(e) => setEditForm({ ...editForm, support_link: e.target.value })}
                  placeholder="https://..."
                  data-testid="edit-support-link-input"
                />
              </div>
              <div>
                <Label htmlFor="edit-email">Email de contact</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editForm.contact_email}
                  onChange={(e) => setEditForm({ ...editForm, contact_email: e.target.value })}
                  placeholder="email@example.com"
                  data-testid="edit-email-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="edit-needs-support"
                  checked={editForm.needs_support}
                  onChange={(e) => setEditForm({ ...editForm, needs_support: e.target.checked })}
                  className="rounded"
                  data-testid="edit-needs-support-checkbox"
                />
                <Label htmlFor="edit-needs-support" className="cursor-pointer">
                  Je recherche du soutien
                </Label>
              </div>
              <div className="flex gap-2 pt-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowEditDialog(false)}
                  className="flex-1"
                >
                  Annuler
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1" 
                  disabled={savingEdit}
                  data-testid="save-edit-button"
                >
                  {savingEdit ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sauvegarde...
                    </>
                  ) : (
                    'Enregistrer'
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
